/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-misused-promises, react-hooks/exhaustive-deps, @typescript-eslint/strict-boolean-expressions */

import { useRecoilValue } from 'recoil';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SettingsPopout from '../../shared/SettingsPopout';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { currentStatsState } from 'states/iVol';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  dayjs,
  getCurrentDate,
  getDefaultSlotProps,
  getQueryDate,
  isBusinessDay,
  isMarketOpenOnDate,
  nextBusinessDay,
  prevBusinessDay,
} from 'util/shared';
import useImpliedVolatility from 'hooks/iVol/useImpliedVolatility';
import useExerciseStyles from 'hooks/iVol/useExerciseStyles';
import {
  DashAccess,
  DatedGreeks,
  GreeksStatsLookback,
  hasDashAccess,
  RawGreeksDataMap,
  RawStatsData,
  StrikeDisplayType,
  VolSkew,
} from 'types';
import {
  MIN_QUERY_DATE,
  SKEW_VIEW_TOOLTIPS,
  convertToDaysTillExpiry,
  extractVolSkewData,
  findClosestGreeksExp,
  findD50Price,
  getExpirationLabel,
  getNextAvailableColor,
  getStatsFromExp,
  getVolSkewTimeframeString,
  getVolSkewTimeframeStringFromLabel,
  mergeObjectLists,
} from 'util/iVol';
import { TimeFramePill } from '../TimeFramePill';
import { userDashAccessState } from 'states';
import StatsLookbackSettings from '../StatsLookbackSetting';
import useClosingPrices from 'hooks/equityhub/useClosingPrices';
import { DEFAULT_BRUSH_ZOOM_CONFIG } from 'config';
import { ZoomOutButton } from '../../shared';
import { DownloadChartButton } from '../../shared/DownloadChartButton';
import { useVolSkewCtx } from './VolSkewContext';
import { serializeLine } from './volSkewLines';

const MAX_TIME_FRAMES = 5;

const VolSkewControls = () => {
  const {
    selectedSym,
    config,
    updateConfig: onConfigChange,
    selectedDatedGreeks: volSkewSelectedDatedGreeks,
    setSelectedDatedGreeks: setVolSkewSelectedDatedGreeks,
    visibleChartLines,
    setVisibleChartLines,
    setVolSkewData,
    isExpirationsSelectorOpen,
    setIsExpirationsSelectorOpen,
    loading,
    error,
  } = useVolSkewCtx();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const defaultSlotProps = getDefaultSlotProps(theme);

  const selectedDisplayType = config.strikeDisplay;
  const showVolSkewIVStats = config.showStats;
  const showSkewDiff = config.showSkewDiff;
  const useSpotGammaIV = config.useSpotGammaIV;
  const dashAccess = useRecoilValue(userDashAccessState);
  const canShowSkewDiff = hasDashAccess(dashAccess, DashAccess.BETA);
  const canShowSkewDiffControl =
    canShowSkewDiff &&
    volSkewSelectedDatedGreeks.filter((dg) =>
      visibleChartLines.includes(dg.key),
    ).length === 2;

  // Controls-internal state for "add timeframe" UI
  const [selectedDate, setSelectedDate] = useState<Dayjs>(getQueryDate(true));
  const [expirations, setExpirations] = useState<string[]>([]);
  const [selectedRawData, setSelectedRawData] = useState<
    RawGreeksDataMap | undefined
  >(undefined);
  const [selectedExpirations, setSelectedExpirations] = useState<string[]>([]);

  const availableColors = Object.values(theme.palette.iVol.volSkew);

  const currentStats = useRecoilValue(currentStatsState);
  const statsDataObj: RawStatsData | null =
    currentStats && currentStats[config.statsLookback];

  const rebuildVolSkewData = (
    datedGreeks: DatedGreeks[],
    displayType: StrikeDisplayType,
  ) => {
    let finalData: VolSkew[] = [];
    datedGreeks.forEach((dg: DatedGreeks) => {
      const transformedVolSkewData: VolSkew[] = extractVolSkewData(
        dg.greeksMap[Number(dg.expiryDate)],
        dg.key,
        dg.statsMap,
        dg.currentPrice,
        hasDashAccess(dashAccess, DashAccess.INTERNAL) && useSpotGammaIV,
        dg.tradeDate,
        dayjs(Number(dg.expiryDate)),
        new Float64Array(0),
        new Float64Array(0),
        exerciseStyles,
      );
      finalData = mergeObjectLists(
        finalData,
        transformedVolSkewData,
        getVolSkewTimeframeStringFromLabel(displayType),
      );
    });
    setVolSkewData(finalData);
  };

  const handleClose = () => {
    setIsExpirationsSelectorOpen(false);
  };

  const handleOpen = () => {
    setIsExpirationsSelectorOpen(true);
  };

  const { getCurrentGreeksData, getDailyGreeksData } = useImpliedVolatility();
  const exerciseStyles = useExerciseStyles(selectedSym);
  const { getClosingPrice } = useClosingPrices();

  const maxQueryDate = getQueryDate(true);

  // Fetch expirations when sym changes so dropdown is populated
  useEffect(() => {
    const fetchExpirations = async () => {
      const rawData = await getCurrentGreeksData(selectedSym);
      if (rawData) {
        setExpirations(Object.keys(rawData));
        setSelectedRawData(rawData);
        setSelectedExpirations([]);
      }
    };
    void fetchExpirations();
  }, [selectedSym, getCurrentGreeksData]);

  const onChangeXDisplayType = (displayType: StrikeDisplayType) => {
    rebuildVolSkewData(volSkewSelectedDatedGreeks, displayType);
    onConfigChange({ strikeDisplay: displayType });
  };

  const onResetToDefault = () => {
    // Clear everything; the load pipeline reseeds the default closest-to-30 line.
    setVolSkewSelectedDatedGreeks([]);
    setVisibleChartLines([]);
    setVolSkewData([]);
    setSelectedExpirations([]);
    setSelectedDate(getQueryDate(true));
    onConfigChange({ lines: [], hiddenKeys: [], asOfDate: undefined });
  };

  const onAddNewTimeFrame = useCallback(async () => {
    let newSelectedDatedGreeks: DatedGreeks[] = [];
    let newData: VolSkew[] = [];
    const usedColors = volSkewSelectedDatedGreeks.map((dg) => dg.color);
    let newlyUsedColors: string[] = [...usedColors];

    const currentPrice = await getClosingPrice(selectedSym);

    selectedExpirations.forEach((expirationDateUtc: string) => {
      const timeFrameKey: string = getVolSkewTimeframeString(
        selectedDate,
        expirationDateUtc,
      );

      if (statsDataObj && selectedRawData) {
        // retrieve the corresponding tte to fetch the right stats entry
        const statsMap = getStatsFromExp(
          statsDataObj,
          selectedRawData,
          expirationDateUtc,
        );

        const fwdPrice = findD50Price(
          selectedRawData[findClosestGreeksExp(selectedRawData, selectedDate)],
        );

        const transformedVolSkewData: VolSkew[] = extractVolSkewData(
          selectedRawData[Number(expirationDateUtc)],
          timeFrameKey,
          statsMap,
          currentPrice ?? fwdPrice,
          hasDashAccess(dashAccess, DashAccess.INTERNAL) && useSpotGammaIV,
          selectedDate,
          dayjs(Number(expirationDateUtc)),
          new Float64Array(0),
          new Float64Array(0),
          exerciseStyles,
        );

        const color =
          getNextAvailableColor(newlyUsedColors, availableColors) ??
          theme.palette.primary.main;

        newlyUsedColors = [...newlyUsedColors, color];
        // updated selected dated greeks to include new expiration
        newSelectedDatedGreeks = [
          ...newSelectedDatedGreeks,
          {
            key: timeFrameKey,
            tradeDate: selectedDate,
            expiryDate: expirationDateUtc,
            greeksMap: selectedRawData,
            statsMap,
            color,
            currentPrice: currentPrice ?? fwdPrice,
            sym: selectedSym,
          },
        ];

        newData = mergeObjectLists(
          newData,
          transformedVolSkewData,
          getVolSkewTimeframeStringFromLabel(selectedDisplayType),
        );
      }
    });

    setVolSkewData((prevData) =>
      mergeObjectLists(
        prevData ?? [],
        newData,
        getVolSkewTimeframeStringFromLabel(selectedDisplayType),
      ),
    );
    setSelectedExpirations([]);
    const allDates = [...volSkewSelectedDatedGreeks, ...newSelectedDatedGreeks];
    setVolSkewSelectedDatedGreeks(allDates);
    onConfigChange({ lines: allDates.map(serializeLine) });
    setVisibleChartLines((prevData: string[]) => [
      ...prevData,
      ...newSelectedDatedGreeks.map(
        (datedGreeks: DatedGreeks) => datedGreeks.key,
      ),
    ]);
  }, [
    selectedDate,
    volSkewSelectedDatedGreeks,
    selectedDisplayType,
    selectedExpirations,
    selectedRawData,
    currentStats,
    setSelectedExpirations,
    setVisibleChartLines,
    setVolSkewSelectedDatedGreeks,
    useSpotGammaIV,
    exerciseStyles,
  ]);

  const onDeleteTimeFrame = useCallback(
    (key: string) => {
      const remainingDGs = volSkewSelectedDatedGreeks.filter(
        (dg: DatedGreeks) => dg.key !== key,
      );
      setVolSkewSelectedDatedGreeks(remainingDGs);
      setVisibleChartLines((prev) => prev.filter((line) => line !== key));
      rebuildVolSkewData(remainingDGs, selectedDisplayType);
      // Removing the last line persists an empty set → pipeline reseeds default.
      onConfigChange({
        lines: remainingDGs.map(serializeLine),
        hiddenKeys: (config.hiddenKeys ?? []).filter((k) => k !== key),
      });
    },
    [
      volSkewSelectedDatedGreeks,
      selectedDisplayType,
      setVolSkewSelectedDatedGreeks,
      setVisibleChartLines,
      onConfigChange,
      config.hiddenKeys,
    ],
  );

  const onToggleLineVisiblity = useCallback(
    (key: string, toShow: boolean) => {
      const updatedVisibleLines: string[] = toShow
        ? [...visibleChartLines, key]
        : visibleChartLines.filter((line: string) => line !== key);
      setVisibleChartLines(updatedVisibleLines);

      const visibleDGs = volSkewSelectedDatedGreeks.filter((dg) =>
        updatedVisibleLines.includes(dg.key),
      );
      rebuildVolSkewData(visibleDGs, selectedDisplayType);

      const allKeys = volSkewSelectedDatedGreeks.map((dg) => dg.key);
      const updatedHiddenKeys = allKeys.filter(
        (k) => !updatedVisibleLines.includes(k),
      );
      onConfigChange({ hiddenKeys: updatedHiddenKeys });
    },
    [
      visibleChartLines,
      setVisibleChartLines,
      volSkewSelectedDatedGreeks,
      selectedDisplayType,
      onConfigChange,
    ],
  );

  // called when user changes an expiration date for a specific trade date
  const onChangeExpiry = useCallback(
    (event: SelectChangeEvent<typeof selectedExpirations>) => {
      const val = event.target.value;
      const updatedExpiries = typeof val === 'string' ? val.split(',') : val;
      setSelectedExpirations(updatedExpiries);
    },
    [setSelectedExpirations],
  );

  const isExpiryDisabled = useCallback(
    (tradeDate: Dayjs, expiry: string) =>
      volSkewSelectedDatedGreeks.some(
        (datedGreeks: DatedGreeks) =>
          datedGreeks.expiryDate === expiry &&
          datedGreeks.tradeDate.isSame(tradeDate, 'day'),
      ),
    [volSkewSelectedDatedGreeks],
  );

  const onTradeDateChange = useCallback(
    async (newValue: Dayjs | null) => {
      if (newValue !== null) {
        const formattedDate: string = newValue.format('YYYY-MM-DD');
        // fetch the relevant greeks based on the selected date
        const rawData: RawGreeksDataMap | null = newValue.isSame(
          getCurrentDate(),
          'day',
        )
          ? await getCurrentGreeksData(selectedSym)
          : await getDailyGreeksData(formattedDate, selectedSym);
        if (rawData) {
          const updatedExpirations: string[] = Object.keys(rawData);

          // update expirations list
          setExpirations(updatedExpirations);
          // update selected expirations to be only the first available expiration by default
          const firstExpiry = updatedExpirations.find(
            (expiry) => !isExpiryDisabled(newValue, expiry),
          );
          setSelectedExpirations(firstExpiry ? [firstExpiry] : []);
          // update currently selected raw greeks data map
          setSelectedRawData(rawData);

          // update selected date
          setSelectedDate(newValue);
        }
      }
    },
    [
      getCurrentGreeksData,
      getDailyGreeksData,
      isExpiryDisabled,
      selectedSym,
      setExpirations,
      setSelectedDate,
      setSelectedExpirations,
      setSelectedRawData,
    ],
  );

  const onStatsLookbackChange = (lookback: GreeksStatsLookback) => {
    onConfigChange({ statsLookback: lookback });

    if (currentStats) {
      const newStatsDataObj: RawStatsData = currentStats[lookback];
      if (newStatsDataObj) {
        let finalData: VolSkew[] = [];
        const updatedDGs = volSkewSelectedDatedGreeks.map((dg) => {
          const statsMap = getStatsFromExp(
            newStatsDataObj,
            dg.greeksMap,
            dg.expiryDate,
          );

          const transformedVolSkewData: VolSkew[] = extractVolSkewData(
            dg.greeksMap[Number(dg.expiryDate)],
            dg.key,
            statsMap,
            dg.currentPrice,
            hasDashAccess(dashAccess, DashAccess.INTERNAL) && useSpotGammaIV,
            dg.tradeDate,
            dayjs(Number(dg.expiryDate)),
            new Float64Array(0),
            new Float64Array(0),
            exerciseStyles,
          );

          if (visibleChartLines.includes(dg.key)) {
            finalData = mergeObjectLists(
              finalData,
              transformedVolSkewData,
              getVolSkewTimeframeStringFromLabel(selectedDisplayType),
            );
          }

          return { ...dg, statsMap };
        });

        setVolSkewSelectedDatedGreeks(updatedDGs);
        setVolSkewData(finalData);
      }
    }
  };

  const reachedMaximumDates =
    volSkewSelectedDatedGreeks?.length === MAX_TIME_FRAMES + 1;

  const tooManyExpirationsSelected =
    selectedExpirations?.length + volSkewSelectedDatedGreeks?.length >
    MAX_TIME_FRAMES + 1;

  const isSelectedDateValid = useMemo(() => {
    return (
      selectedDate &&
      selectedDate.isValid() &&
      isBusinessDay(selectedDate) &&
      selectedDate.isAfter(prevBusinessDay(MIN_QUERY_DATE), 'day') &&
      selectedDate.isBefore(nextBusinessDay(maxQueryDate), 'day')
    );
  }, [maxQueryDate, selectedDate]);

  const datePickerHelpText = useMemo(() => {
    if (reachedMaximumDates) {
      return 'Maximum number of dates used';
    } else if (error) {
      return 'Failed to fetch data for selected date. Try again!';
    } else if (tooManyExpirationsSelected) {
      return 'Too many expirations selected. A maximum of 6 time frames is allowed for better visuals.';
    } else if (selectedDate.isBefore(MIN_QUERY_DATE, 'day')) {
      return `No data available for ${selectedDate.format(
        'YYYY-MM-DD',
      )}. Try another date!`;
    }
    return null;
  }, [error, reachedMaximumDates, selectedDate, tooManyExpirationsSelected]);

  return (
    <Stack sx={{ textAlign: 'left', justifyContent: 'flex-start' }} spacing={5}>
      <ButtonGroup
        aria-label="outlined secondary button group"
        sx={{ alignSelf: 'left', flexWrap: 'wrap' }}
      >
        {Object.values(StrikeDisplayType).map((type: StrikeDisplayType) => (
          <Tooltip
            key={type}
            children={
              <Button
                key={type}
                sx={{
                  backgroundColor:
                    selectedDisplayType === type
                      ? alpha(theme.palette.secondary.main, 0.25)
                      : 'transparent',
                  '&:hover': {
                    color: alpha(theme.palette.secondary.main, 1),
                    borderColor: alpha(theme.palette.secondary.main, 1),
                  },
                  fontSize: isMobile ? 12 : 14,
                  textTransform: 'none',
                  width: '33.3%',
                }}
                onClick={() => {
                  onChangeXDisplayType(type);
                }}
              >
                {type}
              </Button>
            }
            PopperProps={{
              sx: {
                zIndex: 10_001,
                maxWidth: 160,
                '& .MuiTooltip-tooltip': {
                  fontSize: '12px',
                },
              },
            }}
            title={SKEW_VIEW_TOOLTIPS[type]}
          />
        ))}
      </ButtonGroup>
      <Box
        display="flex"
        justifyContent="space-between"
        maxWidth="100%"
        alignItems="center"
      >
        <FormControlLabel
          sx={{
            marginLeft: 0,
          }}
          control={
            <Switch
              sx={{
                '& .MuiSwitch-track': {
                  opacity: 0.25,
                  backgroundColor: theme.palette.success.main,
                },
                '&.Mui-checked .MuiSwitch-track': {
                  opacity: 1,
                },
              }}
              color="success"
              checked={showVolSkewIVStats}
              onChange={(_e, checked: boolean) =>
                onConfigChange({ showStats: checked })
              }
            />
          }
          label="Stats"
          labelPlacement="start"
        />
        {hasDashAccess(dashAccess, DashAccess.INTERNAL) && (
          <FormControlLabel
            sx={{
              marginLeft: 0,
            }}
            control={
              <Switch
                sx={{
                  '& .MuiSwitch-track': {
                    opacity: 0.25,
                    backgroundColor: theme.palette.success.main,
                  },
                  '&.Mui-checked .MuiSwitch-track': {
                    opacity: 1,
                  },
                }}
                color="success"
                checked={useSpotGammaIV}
                onChange={(_e, checked) =>
                  onConfigChange({ useSpotGammaIV: checked })
                }
              />
            }
            label="SGIV"
            labelPlacement="start"
          />
        )}
        {canShowSkewDiffControl && (
          <FormControlLabel
            sx={{
              marginLeft: 0,
            }}
            control={
              <Switch
                sx={{
                  '& .MuiSwitch-track': {
                    opacity: 0.25,
                    backgroundColor: theme.palette.success.main,
                  },
                  '&.Mui-checked .MuiSwitch-track': {
                    opacity: 1,
                  },
                }}
                color="success"
                checked={showSkewDiff}
                onChange={(_e, checked: boolean) =>
                  onConfigChange({ showSkewDiff: checked })
                }
              />
            }
            label="Skew Diff"
            labelPlacement="start"
          />
        )}
        <Button
          variant="contained"
          size="small"
          sx={{
            height: '32px',
            fontSize: isMobile ? 12 : 14,
            textTransform: 'capitalize',
          }}
          onClick={onResetToDefault}
        >
          {loading ? (
            <CircularProgress
              style={{
                color: theme.palette.getContrastText(
                  theme.palette.background.default,
                ),
              }}
            />
          ) : (
            'Reset to Default'
          )}
        </Button>
      </Box>

      {showVolSkewIVStats && (
        <StatsLookbackSettings
          value={config.statsLookback}
          onChange={onStatsLookbackChange}
        />
      )}

      <Typography sx={{ fontSize: isMobile ? 12 : 14, marginBottom: '4px' }}>
        Add Additional Trading Time Frames to Compare Skews
      </Typography>
      <Stack direction="row" justifyContent="space-between" gap={1}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            views={['day']}
            label="From Date"
            value={selectedDate}
            disabled={reachedMaximumDates}
            onChange={onTradeDateChange}
            shouldDisableDate={(date: Dayjs) =>
              !isBusinessDay(date) ||
              date.isBefore(MIN_QUERY_DATE, 'day') ||
              !isMarketOpenOnDate(date)
            }
            format="YYYY-MM-DD"
            minDate={MIN_QUERY_DATE}
            maxDate={maxQueryDate}
            slotProps={{
              ...defaultSlotProps,
              textField: {
                ...defaultSlotProps.textField,
                style: {
                  width: '150px',
                  marginBottom: '10px',
                  fontSize: isMobile ? 12 : 14,
                },
                helperText: datePickerHelpText,
                error: error != null,
              },
            }}
          />
        </LocalizationProvider>
        <FormControl
          sx={{
            width: 120,
          }}
          size="small"
        >
          <InputLabel id="expiry-date-select-label">Expiration</InputLabel>
          <Select
            labelId="expiry-date-select-label"
            id="expiry-date-select"
            multiple
            disabled={loading}
            open={isExpirationsSelectorOpen}
            onClose={handleClose}
            onOpen={handleOpen}
            onChange={onChangeExpiry}
            value={selectedExpirations}
            size="small"
            input={
              <OutlinedInput
                label="Expiration"
                sx={{
                  svg: {
                    color: alpha(theme.palette.primary.main, 0.5),
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                  },
                }}
              />
            }
            renderValue={(selected: string[]) =>
              selected
                .map(
                  (expiration: string) =>
                    `${convertToDaysTillExpiry(
                      selectedDate.valueOf(),
                      Number(expiration),
                    )} DTE`,
                )
                .join(', ')
            }
            MenuProps={{
              sx: {
                zIndex: 10_001,
                '& .MuiSelect-icon': {
                  color: theme.palette.text.primary,
                },
              },
              PaperProps: {
                style: {
                  width: '150px',
                  maxHeight: '250px',
                },
              },
            }}
          >
            {loading ? (
              <MenuItem value="">
                <CircularProgress />
              </MenuItem>
            ) : (
              expirations.map((utcTime: string) => {
                return (
                  <MenuItem
                    key={utcTime}
                    value={utcTime}
                    sx={{
                      padding: '10px',
                    }}
                    disabled={isExpiryDisabled(selectedDate, utcTime)}
                  >
                    <Checkbox
                      checked={selectedExpirations.indexOf(utcTime) > -1}
                      style={{
                        padding: '0px 10px 0px 0px',
                      }}
                    />
                    <ListItemText
                      primary={`${getExpirationLabel(utcTime)}`}
                      secondary={`${convertToDaysTillExpiry(
                        selectedDate.valueOf(),
                        Number(utcTime),
                      )} DTE`}
                      primaryTypographyProps={{
                        fontSize: '13px',
                      }}
                    />
                  </MenuItem>
                );
              })
            )}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          size="small"
          disabled={
            loading ||
            error != null ||
            !isSelectedDateValid ||
            reachedMaximumDates ||
            selectedExpirations?.length === 0 ||
            tooManyExpirationsSelected
          }
          sx={{
            width: '35px',
            height: '37px',
            marginLeft: '10px',
            fontFamily: 'Satoshi Complete',
            fontSize: 14,
            textTransform: 'capitalize',
          }}
          onClick={onAddNewTimeFrame}
        >
          {loading ? (
            <CircularProgress
              style={{
                color: theme.palette.getContrastText(
                  theme.palette.background.default,
                ),
              }}
            />
          ) : (
            'Add'
          )}
        </Button>
      </Stack>

      {volSkewSelectedDatedGreeks.length > 0 ? (
        <Divider
          style={{
            borderColor: theme.palette.getContrastText(
              theme.palette.background.default,
            ),
          }}
        />
      ) : null}

      <List
        style={{
          marginTop: '5px',
        }}
      >
        {volSkewSelectedDatedGreeks.map((datedGreeks: DatedGreeks) => (
          <TimeFramePill
            key={datedGreeks.key}
            identifier={datedGreeks.key}
            onDelete={onDeleteTimeFrame}
            visibleLines={visibleChartLines}
            onToggleLineVisiblity={onToggleLineVisiblity}
            icon={<ShowChartIcon />}
            bgColor={datedGreeks.color}
            expirationDateUtc={datedGreeks.expiryDate}
            date={datedGreeks.tradeDate}
          />
        ))}
      </List>
    </Stack>
  );
};

export const VolSkewChartSettings = () => {
  const { isExpirationsSelectorOpen } = useVolSkewCtx();
  return (
    <SettingsPopout
      title="Vol Skew Settings"
      popperID="vol-skew-controls"
      placement="bottom-end"
      externalMenuOpen={isExpirationsSelectorOpen}
      sx={{
        width: {
          xs: '350px',
          md: '430px',
        },
      }}
    >
      <VolSkewControls />
    </SettingsPopout>
  );
};

export const VolSkewZoomOutButton = () => {
  const { zoomConfig, setZoomConfig, chartData } = useVolSkewCtx();
  return (
    <ZoomOutButton
      zoomConfig={zoomConfig}
      setZoomConfig={setZoomConfig}
      initialData={chartData}
      overrideDefault={{
        leftIdx: DEFAULT_BRUSH_ZOOM_CONFIG.leftIdx,
        rightIdx: chartData.length - 1,
      }}
    />
  );
};

export const VolSkewDownloadButton = () => {
  const { selectedSym, chartRef } = useVolSkewCtx();
  return (
    <DownloadChartButton
      chartRef={chartRef}
      selectedSym={selectedSym}
      chartName="Volatility-Skew"
    />
  );
};
